import React from "react";
import IconButton from "@material-ui/core/IconButton";
import InfoIcon from "@material-ui/icons/Info";
import BarChartIcon from "@material-ui/icons/BarChart";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import { green, red, grey } from "@material-ui/core/colors";
import { WorkerChannelResponse } from "../models/TwilioModels";

interface WorkerCardProps {
	// SID of the worker which is shown on the card.
	sid: string;
	// Full Name, or if it's not available, Friendly Name shown for the worker
	friendlyName: string;
	// General availability of the worker
	available?: boolean;
	// When the card is extended, the response containing channels for this worker is loaded and stored in this object.
	workerChannels?: WorkerChannelResponse[];
	// Function which generates the ReactNode for viewing worker Channels from given properties.
	workerChannelsViewer: (
		// Worker SID which is passed from this component to WorkerChannelsViewer.
		workerSid: string,
		// Response obtained when loading channel data, passed from this component to WorkerChannelsViewer.
		workerChannels?: WorkerChannelResponse[],
		// The function which loads the actual channel data for the worker, which is passed from this component to WorkerChannelsViewer.
		loadWorkerChannelsData?: (workerSid: string) => () => Promise<any>,
	) => React.ReactNode;
	// The function which loads the actual channel data for the worker.
	loadWorkerChannelsFunction: (workerSid: string) => () => Promise<any>;
}

/**
 * Accepts SID, Friendly Name and general availability of the worker to show on the card for the worker, and array of
 * WorkerChannelResponse objects, each containing a name of the channel, the capacity current worker has for this channel,
 * and worker's availability for that particular channel.
 * @param props
 */
export const WorkerCard = (props: WorkerCardProps) => {
	const [expanded, setLocalExpanded] = React.useState(false);
	const [workerChannelsSection, setWorkerChannelsSection] = React.useState<React.ReactNode>(
		expanded
			? props.workerChannelsViewer(props.sid, props.workerChannels, props.loadWorkerChannelsFunction)
			: undefined,
	);

	function handleExpandClick() {
		if (!workerChannelsSection) {
			setWorkerChannelsSection(
				props.workerChannelsViewer(props.sid, props.workerChannels, props.loadWorkerChannelsFunction),
			);
		}
		setLocalExpanded(!expanded);
	}

	function handleShowLogsClick() {}

	return (
		<div
			style={{
				borderWidth: 4,
				borderRadius: 4,
				boxShadow: `-1px 1px 3px ${grey[500]}`,
				margin: 5,
				padding: 8,
			}}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: 2,
					paddingLeft: 5,
				}}
			>
				<span>
					<span
						style={{
							fontSize: 18,
							paddingRight: 4,
						}}
						data-testid="card-friendly-name"
					>
						{props.friendlyName}
					</span>

					<span
						style={{
							fontSize: 12,
							fontWeight: 500,
						}}
						data-testid="card-sid"
					>
						({props.sid})
					</span>
				</span>

				<span
					style={{
						marginRight: 4,
					}}
				>
					<span>
						<IconButton
							title="View worker channels"
							size="small"
							color={"default"}
							onClick={handleExpandClick}
							data-testid="card-channels-button"
							style={{
								minWidth: 8,
								borderRadius: 4,
							}}
						>
							<InfoIcon color="action" fontSize="small" />
						</IconButton>
					</span>

					<span>
						<IconButton
							title="Show worker logs"
							size="small"
							color="default"
							onClick={handleShowLogsClick}
							data-testid="card-logs-button"
							style={{
								minWidth: 8,
								borderRadius: 4,
							}}
						>
							<BarChartIcon color="action" fontSize="small" />
						</IconButton>
					</span>

					<span
						style={{
							paddingRight: 4,
							marginLeft: 6,
						}}
					>
						Available:
					</span>
					<span>
						{props.available === true ? (
							<CheckCircleIcon style={{ color: green[500] }} fontSize="small" />
						) : (
							<CancelIcon style={{ color: red[500] }} fontSize="small" />
						)}
					</span>
				</span>
			</div>

			<div
				style={{
					backgroundColor: grey[200],
					borderRadius: 4,
				}}
				hidden={!expanded}
				data-testid="card-channels-viewer"
			>
				{workerChannelsSection}
			</div>
		</div>
	);
};
